import { useMemo } from 'react'

import { keepPreviousData } from '@tanstack/react-query'
import {
  SearchAIChatbotFAQsQuery,
  SearchAIChatbotFAQsGqlQuery,
  SearchAIChatbotFAQsGqlQueryVariables,
  CreateAIChatbotFAQGqlMutation,
  CreateAIChatbotFAQGqlMutationVariables,
  UpdateAIChatbotFAQGqlMutation,
  UpdateAIChatbotFAQGqlMutationVariables,
  DeleteAIChatbotFAQGqlMutation,
  DeleteAIChatbotFAQGqlMutationVariables,
  DismissAIChatbotFAQMutation,
  DismissAIChatbotFAQMutationVariables,
  GetAIChatbotFAQWithSessionsQuery,
  GetAIChatbotFAQWithSessionsQueryVariables,
  GetUnansweredAIChatbotFAQsCountQuery,
  GetUnansweredAIChatbotFAQsCountQueryVariables,
  AnswerAIChatbotFAQMutationVariables,
  AnswerAIChatbotFAQMutation,
} from 'types/graphql'

import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import { QueryKey, useMutation, useQuery } from 'src/utils/queryClient'

const SearchAIChatbotFAQs = gql`
  query SearchAIChatbotFAQsGqlQuery($input: SearchAIChatbotFAQsQuery!) {
    searchAIChatbotFAQs(input: $input) {
      data {
        ...AIChatbotFAQFragment
      }
      count
    }
  }
  fragment AIChatbotFAQFragment on AIChatbotFAQ {
    answer
    count
    createdAt
    createdByUser {
      ...UserDataFragment
    }
    id
    question
    updatedAt
    updatedByUser {
      ...UserDataFragment
    }
  }
`

export const useSearchAIChatbotFAQsQuery = (args: SearchAIChatbotFAQsQuery) => {
  const { data, ...rest } = useQuery<
    SearchAIChatbotFAQsGqlQuery,
    SearchAIChatbotFAQsGqlQueryVariables
  >({
    placeholderData: keepPreviousData,
    queryKey: [QueryKey.SearchAIChatbotFAQs, args],
    queryDocument: SearchAIChatbotFAQs,
    variables: {
      input: args,
    },
    enabled: !!args.organizationId,
  })
  const faqs = useMemo(() => data?.searchAIChatbotFAQs?.data || [], [data])
  const count = useMemo(() => data?.searchAIChatbotFAQs?.count || 0, [data])
  return {
    ...rest,
    faqs,
    count,
  }
}

const GetAIChatbotFAQWithSessions = gql`
  query GetAIChatbotFAQWithSessionsQuery($id: Int!) {
    aiChatbotFAQ(id: $id) {
      id
      count
      mostRecentConnectedSessions {
        ...AIChatSessionFragment
      }
    }
  }
`

export const useGetAIChatbotFAQWithSessionsQuery = (id: number, enabled: boolean) => {
  return useQuery<GetAIChatbotFAQWithSessionsQuery, GetAIChatbotFAQWithSessionsQueryVariables>({
    queryKey: [QueryKey.GetAIChatbotFAQWithSessions, id],
    queryDocument: GetAIChatbotFAQWithSessions,
    variables: { id },
    enabled,
  })
}

const CreateAIChatbotFAQ = gql`
  mutation CreateAIChatbotFAQGqlMutation($input: AIChatbotFAQInput!) {
    createAIChatbotFAQ(input: $input) {
      id
    }
  }
`

export const useCreateAIChatbotFAQMutation = () => {
  return useMutation<CreateAIChatbotFAQGqlMutation, CreateAIChatbotFAQGqlMutationVariables>({
    mutationDocument: CreateAIChatbotFAQ,
  })
}

const UpdateAIChatbotFAQ = gql`
  mutation UpdateAIChatbotFAQGqlMutation($id: Int!, $input: AIChatbotFAQInput!) {
    updateAIChatbotFAQ(id: $id, input: $input) {
      id
    }
  }
`
export const useUpdateAIChatbotFAQMutation = () => {
  return useMutation<UpdateAIChatbotFAQGqlMutation, UpdateAIChatbotFAQGqlMutationVariables>({
    mutationDocument: UpdateAIChatbotFAQ,
  })
}

const DeleteAIChatbotFAQ = gql`
  mutation DeleteAIChatbotFAQGqlMutation($id: Int!) {
    deleteAIChatbotFAQ(id: $id)
  }
`

export const useDeleteAIChatbotFAQMutation = () => {
  return useMutation<DeleteAIChatbotFAQGqlMutation, DeleteAIChatbotFAQGqlMutationVariables>({
    mutationDocument: DeleteAIChatbotFAQ,
  })
}

const AnswerAIChatbotFAQ = gql`
  mutation AnswerAIChatbotFAQMutation($id: Int!, $input: AnswerAIChatbotFAQInput!) {
    answerAIChatbotFAQ(id: $id, input: $input) {
      id
    }
  }
`

export const useAnswerAIChatbotFAQMutation = () => {
  return useMutation<AnswerAIChatbotFAQMutation, AnswerAIChatbotFAQMutationVariables>({
    mutationDocument: AnswerAIChatbotFAQ,
  })
}

const DismissAIChatbotFAQ = gql`
  mutation DismissAIChatbotFAQMutation($id: Int!) {
    dismissAIChatbotFAQ(id: $id)
  }
`

export const useDismissAIChatbotFAQMutation = () => {
  return useMutation<DismissAIChatbotFAQMutation, DismissAIChatbotFAQMutationVariables>({
    mutationDocument: DismissAIChatbotFAQ,
  })
}

const GetUnansweredAIChatbotFAQsCount = gql`
  query GetUnansweredAIChatbotFAQsCountQuery($organizationId: Int!) {
    unansweredAIChatbotFAQsCount(organizationId: $organizationId)
  }
`

export const useGetUnansweredAIChatbotFAQsCountQuery = (args?: { enabled?: boolean }) => {
  const organizationId = useMyOrgId()
  const { data, ...rest } = useQuery<
    GetUnansweredAIChatbotFAQsCountQuery,
    GetUnansweredAIChatbotFAQsCountQueryVariables
  >({
    queryKey: [QueryKey.GetUnansweredAIChatbotFAQsCount, organizationId],
    queryDocument: GetUnansweredAIChatbotFAQsCount,
    variables: { organizationId: organizationId ?? -1 },
    enabled: !!organizationId && (args?.enabled ?? true),
  })
  const count = useMemo(() => data?.unansweredAIChatbotFAQsCount || 0, [data])
  return {
    ...rest,
    count,
  }
}
